import React, { useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';

export interface RowAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Destructive: shown in red, so it is not chosen by habit. */
  danger?: boolean;
  /** Hidden rather than disabled when it does not apply to this row. */
  hidden?: boolean;
}

/**
 * The ⋮ menu at the end of a row, for whatever a row can do besides open its detail.
 *
 * One menu instead of an icon per action: a column of pencils and bins competes with the
 * row's own click, and an edit icon on a row that already opens the editor is the same
 * action twice. Clicks here never reach the row.
 */
const RowActionsMenu: React.FC<{ name: string; actions: RowAction[]; ariaLabel?: string }> = ({ name, actions, ariaLabel }) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const visible = actions.filter(action => !action.hidden);
  if (!visible.length) return null;

  return (
    <span onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
      <IconButton
        aria-label={ariaLabel ?? t('table.moreActions', { name })}
        aria-haspopup="menu"
        onClick={event => setAnchor(event.currentTarget)}
        sx={{ width: 36, height: 36, borderRadius: '10px' }}
      >
        <MoreVertIcon sx={{ color: 'text.secondary' }} />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {visible.map(action => (
          <MenuItem
            key={action.label}
            disabled={action.disabled}
            onClick={() => {
              setAnchor(null);
              action.onClick();
            }}
            sx={action.danger ? { color: 'error.main' } : undefined}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </span>
  );
};

export default RowActionsMenu;
